import {observer} from "mobx-react";
import {useContext, useState} from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import {useNavigate} from "react-router";
import {Frame} from "../common/Frame";
import {AppContext} from "../../stores/AppStore";
import {D} from "../../Constants";
import {proto} from "../../proto/messages";
import {ErrorUtils} from "../../utils/ErrorUtils";
import {AppRoutes} from "../../AppRoutes";

interface CreateLessonDialogProps {
  open: boolean;
  onClose: () => void;
  onCreated?: () => void;
}

export const CreateLessonDialog = observer(({open, onClose, onCreated}: CreateLessonDialogProps) => {
  const app = useContext(AppContext)!;
  const navigate = useNavigate();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [creating, setCreating] = useState(false);

  const canCreate = title.trim().length > 0;

  const handleCreate = async () => {
    if (!canCreate) return;

    setCreating(true);
    try {
      const response = await app.socketApi.fetch(
        proto.TxCreateLesson.create({
          title: title.trim(),
          description: description.trim(),
        }),
        new proto.RxCreateLessonResponse(),
      );

      if (response.proto.lessonId) {
        onCreated?.();
        handleClose();
        navigate(AppRoutes.lessonEditorPath(response.proto.lessonId));
      }
    } catch (e) {
      ErrorUtils.fromFetch(e);
    } finally {
      setCreating(false);
    }
  };

  const handleClose = () => {
    setTitle('');
    setDescription('');
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={(_, reason) => {
        if (reason !== 'backdropClick') handleClose();
      }}
      maxWidth="sm"
      fullWidth
      slotProps={{
        paper: {
          style: {
            borderRadius: D.radius(2),
          }
        }
      }}
    >
      {/* Header */}
      <Frame
        row
        alignItems="center"
        justifyContent="space-between"
        padding={3}
        style={{paddingBottom: D.unit(2)}}
      >
        <Typography variant="h4">
          Create Lesson
        </Typography>
        <IconButton onClick={handleClose}>
          <CloseIcon/>
        </IconButton>
      </Frame>

      <DialogContent style={{paddingTop: 0}}>
        <Frame direction="column" gap={2}>
          {/* Title */}
          <Frame direction="column" gap={1}>
            <Typography variant="caption" fontWeight={600} style={{letterSpacing: '0.5px'}}>
              TITLE
            </Typography>
            <TextField
              fullWidth
              size="small"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Enter lesson title"
              disabled={creating}
              autoFocus
            />
          </Frame>

          {/* Description */}
          <Frame direction="column" gap={1}>
            <Typography variant="caption" fontWeight={600} style={{letterSpacing: '0.5px'}}>
              DESCRIPTION (OPTIONAL)
            </Typography>
            <TextField
              fullWidth
              size="small"
              multiline
              minRows={3}
              maxRows={8}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Enter lesson description"
              disabled={creating}
            />
          </Frame>
        </Frame>
      </DialogContent>

      <DialogActions style={{padding: D.unit(3), paddingTop: D.unit(2)}}>
        <Button onClick={handleClose} disabled={creating}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleCreate}
          disabled={!canCreate || creating}
        >
          {creating ? 'Creating...' : 'Create Lesson'}
        </Button>
      </DialogActions>
    </Dialog>
  );
});
